import { useRef, useEffect, useState, CSSProperties } from 'react';
import { motion } from 'framer-motion';

const Timeline = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [regiaoAtual, setRegiaoAtual] = useState<string>("");
  const [testMode, setTestMode] = useState(false);
  const [videoPopup, setVideoPopup] = useState({
    isOpen: false,
    videoUrl: ""
  });

  // Links de inscrição específicos por região
  const inscricaoLinks = {
    APAC: "https://app.smartsheet.com/b/form/87c47929ab264d98bc4c4db75461b35e",
    APS: "https://app.smartsheet.com/b/form/6b910ef28703490692b5556b9c7e9ac0",
    NORDESTE: "https://app.smartsheet.com/b/form/b125c348d060496e8e3815eed0374bf3",
    default: "#register" // Link padrão caso nenhuma região seja detectada
  };

  // Links para envio do Canvas por região
  const canvasLinks = {
    APAC: "https://app.smartsheet.com/b/form/b05353bda3074cdda7404d31898f504e",
    APS: "https://app.smartsheet.com/b/form/f08fa1bed4194eb5807e794b54b987ce",
    NORDESTE: "https://app.smartsheet.com/b/form/8708b4f5b1cd4df982d4de8a4318ecda",
    default: "#" // Link padrão caso nenhuma região seja detectada
  };

  // Links para envio do Storyboard por região
  const storyboardLinks = {
    APAC: "https://app.smartsheet.com/b/form/f57aa7b9917a4aa88927d0cea6c20b7c",
    APS: "https://app.smartsheet.com/b/form/d142e95397994906b49fa73e0bef866a",
    NORDESTE: "https://app.smartsheet.com/b/form/c5168c3d2c8c4123974d937558b193b6",
    default: "#" // Link padrão caso nenhuma região seja detectada
  };

  // Links para envio do Pitch por região
  const pitchLinks = {
    APAC: "https://app.smartsheet.com/b/form/329227a3a85644319ffa3018a954cd78",
    APS: "https://app.smartsheet.com/b/form/504270923d014ac686e72eb3392c1d02",
    NORDESTE: "https://app.smartsheet.com/b/form/ba5e2b95b2434f309a7c3feef709bc32",
    default: "#" // Link padrão caso nenhuma região seja detectada
  };

  // Links dos vídeos
  const videoLinks = {
    canvas: "https://vimeo.com/1074392255",
    storyboard: "https://vimeo.com/1074383849",
    pitch: "https://vimeo.com/1074401947"
  };

  // Verificar se o modo de teste está ativo via URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('test_mode') || urlParams.has('teste')) {
      setTestMode(true);
    }
  }, []);

  // Obter a região atual do atributo data-regiao
  useEffect(() => {
    const detectarRegiao = () => {
      const regiao = document.documentElement.getAttribute('data-regiao');
      if (regiao) {
        setRegiaoAtual(regiao.toUpperCase());
      }
    };

    detectarRegiao();

    // Observar mudanças no atributo data-regiao
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' && 
          mutation.attributeName === 'data-regiao'
        ) {
          detectarRegiao();
        }
      });
    });
    
    // Iniciar observação
    observer.observe(document.documentElement, { attributes: true });
    
    // Limpeza ao desmontar
    return () => {
      observer.disconnect();
    };
  }, []);

  // Função para verificar se uma data está dentro de um período
  const isDateInRange = (startDateStr: string, endDateStr: string): boolean => {
    // Se o modo de teste estiver ativo, considerar todas as datas como dentro do período
    if (testMode) return true;
    
    // Converter as strings de data para objetos Date com o fuso horário de Brasília (UTC-3)
    const [startDay, startMonth, startYear] = startDateStr.split('/').map(Number);
    const [endDay, endMonth, endYear] = endDateStr.split('/').map(Number);
    
    // Criar datas no fuso horário local
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    endDate.setHours(23, 59, 59, 999); // Definir para o final do dia
    
    // Ajustar para o fuso horário de Brasília se não estiver já nele
    const offset = -3 * 60 - new Date().getTimezoneOffset(); // Diferença em minutos para UTC-3 (Brasília)
    
    // Criar uma cópia da data atual ajustada para o fuso horário de Brasília
    const brasiliaDate = new Date(currentDate.getTime() + offset * 60000);
    
    return brasiliaDate >= startDate && brasiliaDate <= endDate;
  };

  // Função para verificar se uma data é anterior a uma data de início
  const isDateBeforeRange = (startDateStr: string): boolean => {
    // Se o modo de teste estiver ativo, considerar todas as datas como dentro do período
    if (testMode) return false;
    
    const [startDay, startMonth, startYear] = startDateStr.split('/').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay);
    
    // Ajustar para o fuso horário de Brasília se não estiver já nele
    const offset = -3 * 60 - new Date().getTimezoneOffset(); // Diferença em minutos para UTC-3 (Brasília)
    
    // Criar uma cópia da data atual ajustada para o fuso horário de Brasília
    const brasiliaDate = new Date(currentDate.getTime() + offset * 60000);
    
    return brasiliaDate < startDate;
  };

  // Função para obter o link de inscrição com base na região atual
  const getInscricaoLink = (): string => {
    if (regiaoAtual === "APAC") {
      return inscricaoLinks.APAC;
    } else if (regiaoAtual === "APS") {
      return inscricaoLinks.APS;
    } else if (regiaoAtual === "NORDESTE") {
      return inscricaoLinks.NORDESTE;
    } else {
      return inscricaoLinks.default;
    }
  };

  // Função para obter o link de Canvas com base na região atual
  const getCanvasLink = (): string => {
    if (regiaoAtual === "APAC") {
      return canvasLinks.APAC;
    } else if (regiaoAtual === "APS") {
      return canvasLinks.APS;
    } else if (regiaoAtual === "NORDESTE") {
      return canvasLinks.NORDESTE;
    } else {
      return canvasLinks.default;
    }
  };

  // Função para obter o link de Storyboard com base na região atual
  const getStoryboardLink = (): string => {
    if (regiaoAtual === "APAC") {
      return storyboardLinks.APAC;
    } else if (regiaoAtual === "APS") {
      return storyboardLinks.APS;
    } else if (regiaoAtual === "NORDESTE") {
      return storyboardLinks.NORDESTE;
    } else {
      return storyboardLinks.default;
    }
  };

  // Função para obter o link de Pitch com base na região atual
  const getPitchLink = (): string => {
    if (regiaoAtual === "APAC") {
      return pitchLinks.APAC;
    } else if (regiaoAtual === "APS") {
      return pitchLinks.APS;
    } else if (regiaoAtual === "NORDESTE") {
      return pitchLinks.NORDESTE;
    } else {
      return pitchLinks.default;
    }
  };

  // Verificar se cada período de inscrição está ativo
  const isInscricaoAtiva = isDateInRange("14/04/2025", "09/05/2025");
  const isInscricaoFutura = isDateBeforeRange("14/04/2025");
  const isCanvasAtivo = true; // Sempre ativo
  const isStoryboardAtivo = true; // Sempre ativo
  const isPitchAtivo = true; // Sempre ativo

  // Função para abrir popup de vídeo
  const openVideoPopup = (videoType: "canvas" | "storyboard" | "pitch") => {
    setVideoPopup({
      isOpen: true,
      videoUrl: videoLinks[videoType]
    });
    // Evitar rolagem do body quando o popup está aberto
    document.body.style.overflow = 'hidden';
  };

  // Função para fechar popup de vídeo
  const closeVideoPopup = () => {
    setVideoPopup({
      isOpen: false,
      videoUrl: ""
    });
    // Restaurar rolagem do body
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    // Atualizar a data atual a cada minuto
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    const currentRef = ref.current;
    
    // Verificar tamanho da tela inicialmente
    const checkScreenSize = () => {
      const mobileView = window.innerWidth < 768;
      setIsLargeScreen(window.innerWidth >= 1280);
      setIsMediumScreen(window.innerWidth >= 768 && window.innerWidth < 1280);
      setIsMobile(mobileView);
      
      // Se for mobile, mostrar tudo imediatamente
      if (mobileView) {
        setIsInView(true);
      }
    };
    checkScreenSize();
    
    // Adicionar listener para redimensionamento
    window.addEventListener('resize', checkScreenSize);

    // Configurar o observer para detectar quando o componente está visível
    let observer: IntersectionObserver | null = null;
    
    // Pequeno atraso para evitar problemas de timing
    const timer2 = setTimeout(() => {
      if (currentRef && !isMobile) {
        // Definir um threshold menor para dispositivos médios
        const threshold = 0.3;
        
        observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            setIsInView(entry.isIntersecting);
          },
          { 
            threshold: threshold,
            rootMargin: '0px'
          }
        );
        
        observer.observe(currentRef);
      }
    }, 100);

    return () => {
      clearInterval(timer);
      clearTimeout(timer2);
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [isMobile]);

  const events = [
    {
      title: "1 – Inscrição",
      date: "14/04/2025 a 09/05/2025",
      description: "Inscreva sua equipe clicando no botão abaixo. Leia atentamente o regulamento e se atente ao prazo.",
      color: "linear-gradient(to right, #3b82f6, #4f46e5)",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      title: "2 – Envio do CANVAS",
      date: "28/04/2025 a 23/05/2025",
      description: "Faça um brainstorm com a sua dupla e com as ideias apresentadas o Canvas deverá ser preenchido. Abaixo deixamos disponível o CANVAS e também um vídeo que irá auxiliar vocês no preenchimento. Se atente ao prazo.",
      color: "linear-gradient(to right, #a855f7, #db2777)",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 16L8.58579 11.4142C9.36683 10.6332 10.6332 10.6332 11.4142 11.4142L16 16M14 14L15.5858 12.4142C16.3668 11.6332 17.6332 11.6332 18.4142 12.4142L20 14M14 8H14.01M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      title: "3 – Envio do STORYBOARD",
      date: "26/05/2025 a 13/06/2025",
      description: "A partir do Canvas a equipe deverá desenvolver um protótipo de baixa fidelidade na ferramenta STORYBOARD, criando cada tela e funcionalidades do aplicativo. Abaixo deixamos um vídeo explicando como preencher o seu STORYBOARD e o arquivo que deverá ser utilizado. Se atente ao prazo.",
      color: "linear-gradient(to right, #06b6d4, #0284c7)",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      title: "4 – Envio do PITCH",
      date: "16/06/2025 a 15/08/2025",
      description: "Após todo o desenvolvimento do aplicativo chegou a hora de apresentar a ideia escolhida. Grave um vídeo no formato horizontal de no máximo 2 minutos e venda sua ideia explicando o por que da escolha e apresentando os diferenciais. Abaixo tem um vídeo que irá auxiliar na hora de gravar o PITCH e também um material com os critérios que serão avaliados.",
      color: "linear-gradient(to right, #ffb03f, #f59e0b)",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 15C15.866 15 19 11.866 19 8C19 4.13401 15.866 1 12 1C8.13401 1 5 4.13401 5 8C5 11.866 8.13401 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    }
  ];

  // Decorações para o fundo
  const decorations = [
    { top: "10%", left: "5%", size: "64px", color: "rgba(59, 130, 246, 0.1)", delay: 0.2 },
    { top: "30%", right: "8%", size: "96px", color: "rgba(249, 115, 22, 0.1)", delay: 0.3 },
    { top: "70%", left: "12%", size: "80px", color: "rgba(168, 85, 247, 0.1)", delay: 0.4 },
    { bottom: "15%", right: "15%", size: "128px", color: "rgba(79, 70, 229, 0.1)", delay: 0.5 }
  ];

  const sectionStyle: CSSProperties = {
    position: 'relative',
    padding: '3rem 0 5rem',
    overflow: 'hidden',
    backgroundColor: '#f9fafb',
  };

  const containerStyle: CSSProperties = {
    width: '100%',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1.5rem',
    position: 'relative',
    zIndex: 10,
  };

  const textCenterStyle: CSSProperties = {
    textAlign: 'center',
    marginBottom: '4rem',
  };

  const tagStyle: CSSProperties = {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: '9999px',
    padding: '0.25rem 1rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    display: 'inline-block',
    marginBottom: '1rem',
  };

  const headingStyle: CSSProperties = {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '1rem',
    fontFamily: 'Kobe, sans-serif'
  };

  const dividerStyle: CSSProperties = {
    height: '0.25rem',
    width: '6rem',
    background: 'linear-gradient(to right, #f97316, #fdba74)',
    margin: '0 auto 2rem auto',
    borderRadius: '9999px',
  };

  const descriptionStyle: CSSProperties = {
    fontSize: '1.125rem',
    color: '#4b5563',
    maxWidth: '48rem',
    margin: '0 auto',
  };

  const gridContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isLargeScreen 
      ? 'repeat(2, 1fr)' 
      : (isMediumScreen 
          ? 'repeat(2, 1fr)' 
          : 'repeat(1, 1fr)'),
    gap: '2.5rem',
    maxWidth: '80rem',
    margin: '0 auto',
  };

  const noticeContainerStyle: CSSProperties = {
    marginTop: '4rem',
    maxWidth: '48rem',
    margin: '4rem auto 0 auto',
  };

  const noticeStyle: CSSProperties = {
    padding: '1.5rem',
    background: 'linear-gradient(to right, rgba(219, 234, 254, 0.5), rgba(224, 231, 255, 0.5))',
    borderRadius: '0.75rem',
    border: '1px solid rgba(191, 219, 254, 0.7)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    backdropFilter: 'blur(4px)',
  };

  const noticeFlexStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
  };

  const noticeIconContainerStyle: CSSProperties = {
    flexShrink: 0,
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '9999px',
    backgroundColor: '#dbeafe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const noticeContentStyle: CSSProperties = {
    marginLeft: '1rem',
  };

  const noticeHeadingStyle: CSSProperties = {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#1e3a8a',
    marginBottom: '0.25rem',
  };

  const noticeParagraphStyle: CSSProperties = {
    color: '#4b5563',
  };

  const buttonStyle: CSSProperties = {
    padding: '0.75rem 1.25rem',
    borderRadius: '0.5rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    display: 'inline-block',
  };

  const primaryButtonStyle: CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#2563eb',
    color: 'white',
    border: '1px solid transparent',
  };

  const secondaryButtonStyle: CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'white',
    color: '#2563eb',
    border: '1px solid #d1d5db',
  };

  const disabledButtonStyle: CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#9ca3af',
    color: 'white',
    border: '1px solid transparent',
    cursor: 'not-allowed',
    opacity: 0.7,
  };

  const buttonContainerStyle: CSSProperties = {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.25rem',
  };

  return (
    <section id="timeline" style={sectionStyle} ref={ref}>
      {/* Video Popup */}
      {videoPopup.isOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '2rem',
            boxSizing: 'border-box'
          }}
          onClick={closeVideoPopup}
        >
          <div 
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '1000px',
              aspectRatio: '16/9'
            }}
            onClick={e => e.stopPropagation()}
          >
            <iframe 
              src={`${videoPopup.videoUrl.replace('https://vimeo.com/', 'https://player.vimeo.com/video/')}?autoplay=1&title=0&byline=0&portrait=0`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '0.5rem'
              }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
            <button 
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                backgroundColor: 'white',
                color: 'black',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
              onClick={closeVideoPopup}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Elementos decorativos */}
      {decorations.map((decoration, index) => (
        <motion.div
          key={index}
          initial={isMobile ? { scale: 1, opacity: 0.3 } : { scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 0.3 }}
          viewport={{ once: true }}
          transition={{ 
            duration: isMobile ? 0 : 0.8, 
            delay: isMobile ? 0 : decoration.delay,
            type: "spring",
            stiffness: 50
          }}
          style={{
            position: 'absolute',
            width: decoration.size,
            height: decoration.size,
            borderRadius: '50%',
            backgroundColor: decoration.color,
            top: decoration.top || undefined,
            left: decoration.left || undefined,
            right: decoration.right || undefined,
            bottom: decoration.bottom || undefined,
            zIndex: 0,
          }}
        />
      ))}
      
      <div style={containerStyle}>
        <motion.div
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0 : 0.6 }}
          style={textCenterStyle}
        >
          <h2 style={headingStyle} className="title-font">Cronograma do Evento</h2>
          <div style={dividerStyle}></div>
          <p style={descriptionStyle}>
            Fique atento às datas importantes do Hackathon Happy Escolas 2025 para não perder nenhuma etapa!
          </p>
        </motion.div>

        <div style={gridContainerStyle}>
          {events.map((event, index) => (
            <motion.div
              key={index}
              initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ 
                duration: isMobile ? 0 : 0.7, 
                delay: isMobile ? 0 : index * 0.2,
                type: "spring", 
                stiffness: 100 
              }}
              style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transform: 'translateY(0)',
                transition: 'all 0.5s ease',
                border: '1px solid rgba(229, 231, 235, 0.5)',
              }}
            >
              <div 
                style={{
                  background: event.color,
                  padding: '1.5rem',
                  color: 'white',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  height: '7rem',
                  minHeight: '7rem',
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0.2,
                  background: 'radial-gradient(ellipse at top right, white, transparent, transparent)',
                }}></div>
                <div style={{
                  position: 'absolute',
                  width: '3rem',
                  height: '3rem',
                  right: '-1.5rem',
                  top: '-1.5rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                }}></div>
                <div style={{
                  position: 'absolute',
                  width: '5rem',
                  height: '5rem',
                  left: '-2.5rem',
                  bottom: '-2.5rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '50%',
                }}></div>
                
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '0.75rem',
                  padding: '0.75rem',
                  backdropFilter: 'blur(4px)',
                  position: 'relative',
                  zIndex: 10,
                }}>
                  {event.icon}
                </div>
                
                <div style={{
                  marginLeft: '1rem',
                  position: 'relative',
                  zIndex: 10,
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    marginBottom: '0.25rem',
                  }}>{event.title}</h3>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '0.25rem' }}>
                      <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span style={{
                      fontWeight: 500,
                      letterSpacing: '0.025em',
                    }}>{event.date}</span>
                  </div>
                </div>
              </div>
              
              <div style={{
                padding: '1.5rem',
                flexGrow: 1,
                position: 'relative',
                zIndex: 10,
                background: 'linear-gradient(to bottom right, white, #f9fafb)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}>
                <p style={{
                  color: '#4b5563',
                  lineHeight: 1.5,
                  marginBottom: '1rem',
                }}>{event.description}</p>
                
                <div>
                  <div style={buttonContainerStyle}>
                    {index === 0 && (
                      <a 
                        href={isInscricaoAtiva || testMode ? getInscricaoLink() : undefined} 
                        style={{
                          ...(isInscricaoAtiva || testMode ? primaryButtonStyle : disabledButtonStyle), 
                          width: '100%'
                        }}
                        onMouseOver={e => {
                          if (isInscricaoAtiva || testMode) {
                            e.currentTarget.style.backgroundColor = '#1d4ed8';
                          }
                        }}
                        onMouseOut={e => {
                          if (isInscricaoAtiva || testMode) {
                            e.currentTarget.style.backgroundColor = '#2563eb';
                          }
                        }}
                        title={isInscricaoAtiva || testMode ? "" : (isInscricaoFutura ? "O período de inscrições ainda não começou" : "Período de inscrições encerrado")}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {isInscricaoAtiva || testMode 
                          ? "Inscreva-se" 
                          : (isInscricaoFutura 
                              ? "Inscrições ainda não abertas" 
                              : "Inscrições encerradas")
                        }
                      </a>
                    )}
                    
                    {index === 1 && (
                      <>
                        <a 
                          href="#" 
                          style={{...primaryButtonStyle, width: '33%'}}
                          onMouseOver={e => {
                            e.currentTarget.style.backgroundColor = '#1d4ed8';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.backgroundColor = '#2563eb';
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            openVideoPopup("canvas");
                          }}
                        >
                          Vídeo
                        </a>
                        <a 
                          href="/files/canvas design thinking.pdf" 
                          style={{...secondaryButtonStyle, width: '33%'}}
                          onMouseOver={e => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                          download="Canvas Design Thinking.pdf"
                          title="Download do Canvas Design Thinking"
                        >
                          Canvas
                        </a>
                        <a 
                          href={isCanvasAtivo || testMode ? getCanvasLink() : undefined} 
                          style={{
                            ...((isCanvasAtivo || testMode) ? 
                              {...primaryButtonStyle, backgroundColor: '#f97316'} : 
                              disabledButtonStyle),
                            width: '33%'
                          }}
                          onMouseOver={e => {
                            if (isCanvasAtivo || testMode) {
                              e.currentTarget.style.backgroundColor = '#ea580c';
                            } else {
                              e.currentTarget.title = "Fora da data de envio";
                            }
                          }}
                          onMouseOut={e => {
                            if (isCanvasAtivo || testMode) {
                              e.currentTarget.style.backgroundColor = '#f97316';
                            }
                          }}
                          title={(isCanvasAtivo || testMode) ? "" : "Fora da data de envio"}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Enviar
                        </a>
                      </>
                    )}
                    
                    {index === 2 && (
                      <>
                        <a 
                          href="#" 
                          style={{...primaryButtonStyle, width: '33%'}}
                          onMouseOver={e => {
                            e.currentTarget.style.backgroundColor = '#1d4ed8';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.backgroundColor = '#2563eb';
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            openVideoPopup("storyboard");
                          }}
                        >
                          Vídeo
                        </a>
                        <a 
                          href="/files/storyboard app color.png" 
                          style={{...secondaryButtonStyle, width: '33%'}}
                          onMouseOver={e => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                          download="Storyboard App Color.png"
                          title="Download do Storyboard App Color"
                        >
                          Storyboard
                        </a>
                        <a 
                          href={isStoryboardAtivo || testMode ? getStoryboardLink() : undefined} 
                          style={{
                            ...((isStoryboardAtivo || testMode) ? 
                              {...primaryButtonStyle, backgroundColor: '#f97316'} : 
                              disabledButtonStyle),
                            width: '33%'
                          }}
                          onMouseOver={e => {
                            if (isStoryboardAtivo || testMode) {
                              e.currentTarget.style.backgroundColor = '#ea580c';
                            } else {
                              e.currentTarget.title = "Fora da data de envio";
                            }
                          }}
                          onMouseOut={e => {
                            if (isStoryboardAtivo || testMode) {
                              e.currentTarget.style.backgroundColor = '#f97316';
                            }
                          }}
                          title={(isStoryboardAtivo || testMode) ? "" : "Fora da data de envio"}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Enviar
                        </a>
                      </>
                    )}
                    
                    {index === 3 && (
                      <>
                        <a 
                          href="#" 
                          style={{...primaryButtonStyle, width: '33%'}}
                          onMouseOver={e => {
                            e.currentTarget.style.backgroundColor = '#1d4ed8';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.backgroundColor = '#2563eb';
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            openVideoPopup("pitch");
                          }}
                        >
                          Vídeo
                        </a>
                        <a 
                          href="/files/Material de apoio PITCH.pdf" 
                          style={{...secondaryButtonStyle, width: '33%'}}
                          onMouseOver={e => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                          download="Material de apoio PITCH.pdf"
                          title="Download do Material de apoio PITCH"
                        >
                          Critérios
                        </a>
                        <a 
                          href={isPitchAtivo || testMode ? getPitchLink() : undefined} 
                          style={{
                            ...((isPitchAtivo || testMode) ? 
                              {...primaryButtonStyle, backgroundColor: '#f97316'} : 
                              disabledButtonStyle),
                            width: '33%'
                          }}
                          onMouseOver={e => {
                            if (isPitchAtivo || testMode) {
                              e.currentTarget.style.backgroundColor = '#ea580c';
                            } else {
                              e.currentTarget.title = "Fora da data de envio";
                            }
                          }}
                          onMouseOut={e => {
                            if (isPitchAtivo || testMode) {
                              e.currentTarget.style.backgroundColor = '#f97316';
                            }
                          }}
                          title={(isPitchAtivo || testMode) ? "" : "Fora da data de envio"}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Enviar
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <motion.div 
                style={{
                  height: '0.375rem',
                  background: event.color,
                  width: isMobile ? '100%' : '0%',
                  transition: 'width 1.5s ease-out',
                }}
                animate={isInView ? { width: '100%' } : { width: '0%' }}
                transition={{ duration: isMobile ? 0 : 1.5, delay: isMobile ? 0 : index * 0.3 + 0.5 }}
              />
            </motion.div>
          ))}
        </div>
        
        {testMode && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{...noticeContainerStyle, marginTop: '2rem'}}
          >
            <div style={{...noticeStyle, backgroundColor: 'rgba(254, 226, 226, 0.5)', border: '1px solid rgba(254, 202, 202, 0.7)'}}>
              <div style={noticeFlexStyle}>
                <div style={{...noticeIconContainerStyle, backgroundColor: '#fee2e2'}}>
                  <svg width="20" height="20" fill="none" stroke="#dc2626" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                </div>
                <div style={noticeContentStyle}>
                  <h4 style={{...noticeHeadingStyle, color: '#dc2626'}}>Modo de Teste Ativo</h4>
                  <p style={noticeParagraphStyle}>
                    Você está no modo de teste. Todos os botões estão ativos independentemente da data.
                    Este modo é apenas para testes e demonstração do site.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Timeline; 